import { Box, Button } from '@mui/material';
import { X } from 'lucide-react';
import React from 'react';
import { useIntl } from 'react-intl';

import BenchmarkSegmentedToggle from 'views/benchmark-analytics/components/benchmark-segmented-toggle';
import { BenchmarkTimeGrouping } from 'views/benchmark-analytics/types/benchmark-filters.type';
import { DEFAULT_GROWTH_FILTERS } from '../constants/growth';
import { useGrowthState } from '../context/growth-context';
import { GrowthVariant } from '../types/growth-filters.type';
import { snapToPeriod } from '../utils/snap-to-period';
import GrowthDateRangePicker from './growth-date-range-picker';
import GrowthFarmAutocomplete from './growth-farm-autocomplete';

const GROUPINGS: BenchmarkTimeGrouping[] = ['day', 'week', 'month', 'quarter'];
const VARIANTS: GrowthVariant[] = ['fullCycle', 'finalStage'];

const GrowthFilterPanel: React.FC = () => {
  const intl = useIntl();
  const { filters, setFilters, hasActiveFilters } = useGrowthState();
  // The shared inputs call a light surface "dark" (dark text on a white field).
  const label = (id: string) => intl.formatMessage({ id: `benchmarkAnalytics.growthPage.filters.${id}` });
  const changeGrouping = (grouping: BenchmarkTimeGrouping) => {
    const { from, to } = filters.dateRange;
    setFilters({
      ...filters,
      grouping,
      dateRange: { from: snapToPeriod(from, grouping, 'from'), to: snapToPeriod(to, grouping, 'to') },
    });
  };

  return (
    <Box display="flex" flexDirection="column" gap={4}>
      <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-between" gap={4}>
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={4}>
          <BenchmarkSegmentedToggle
            ariaLabel={label('groupingLabel')}
            value={filters.grouping}
            options={GROUPINGS.map((grouping) => ({ value: grouping, label: label(grouping) }))}
            onChange={changeGrouping}
          />
          <BenchmarkSegmentedToggle
            ariaLabel={label('variantLabel')}
            value={filters.variant}
            options={VARIANTS.map((variant) => ({ value: variant, label: label(variant) }))}
            onChange={(variant) => setFilters({ ...filters, variant })}
          />
        </Box>
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={3}>
          <GrowthDateRangePicker />
        </Box>
      </Box>
      <Box display="flex" flexDirection="column" gap={2}>
        {hasActiveFilters && (
          <Box display="flex" justifyContent="flex-end">
            <Button
              sx={{ color: 'common.white' }}
              variant="text"
              size="small"
              startIcon={<X size={16} />}
              onClick={() => setFilters(DEFAULT_GROWTH_FILTERS)}>
              {label('reset')}
            </Button>
          </Box>
        )}
        <GrowthFarmAutocomplete />
      </Box>
    </Box>
  );
};

export default GrowthFilterPanel;
